import React, { useMemo, useState, forwardRef, useImperativeHandle, useRef, useContext, useEffect } from 'react';
import { PaperIcon, Dropdown } from '@nackle/paper';
import { useTranslations } from '../../hooks/translations';
import classNames from 'classnames';
import { NotificationCarousel } from '../../notifications/NotificationCarousel';
import './styles.less';
import moment from 'moment';
import { BellFull, BellEmpty } from './Bell.js';
import { NotificationContext } from '../../notifications/NotificationsContext';
import { getString } from '@nackle/intl-tools';
import { Spin } from 'antd';
import { onResizeEffect } from '../../scripts/utils.js';

function checkIsMobile() {
    return window.innerWidth <= 900;
}

export const NotificationDropdown = forwardRef( ( { onOpenCb, loggingOut, brand }, ref ) => {
    
    const { locale } = useTranslations();

    const carouselRef = useRef();

    const [ open, setOpen ] = useState( false );

    const { notifications, loading, localRead } = useContext( NotificationContext );

    const [ isMobile, setIsMobile ] = useState( checkIsMobile() );

    const htmlDecode = ( input ) => {
        const doc = new DOMParser().parseFromString( input, 'text/html' );
        return doc.documentElement.textContent;
    };
    
    const unreadCount = useMemo( () => {
        const unreadList = ( notifications ?? [] )
            .filter( n => !n.isRead );
        return unreadList.length;
    }, [ notifications ] );

    function close() {
        setOpen( false );
    }

    useImperativeHandle( ref, () => ( {
        close: () => {
            close();
        }
    } ), [] );

    useEffect( () => {
        moment.locale( locale.substring( 0, 2 ) );
    }, [ locale ] );

    function handleClick( noti ) {
        carouselRef.current?.open( noti.id );
    }

    function onResize() {
        setIsMobile( checkIsMobile() );
        close();
    }
    
    useEffect( onResizeEffect( onResize ), [] );

    function toggleOpen() {
        const newOpenState = !open;
        setOpen( newOpenState );
        if ( newOpenState == true ) {
            onOpenCb?.();
        }
    }

    const items = useMemo( () => loading ? [ {
        key: 'loading',
        className: 'loading',
        label: <Spin />
    } ] : notifications == null || notifications.length == 0 ? [ {
        key: 'no-content',
        label: getString( 'notification.dropdown.empty' )
    } ] : notifications.flatMap( ( n, i ) => ( [ {
        key: n.id,
        className: 'clickable',
        label: <span onClick={ () => handleClick( n ) }>
            <span className='header'>
                <h3 dangerouslySetInnerHTML={ { __html: n.name } } />
                { !n.isRead && <PaperIcon icon='Circle2' collection='ultimate' className='alert' /> }
            </span>
            
            <span className='description'>{ htmlDecode( n.description ).replace( /<[^>]+>/g, '' ).trim() }</span> { /* dont show the true content here or else it will look like crap - this scrapes the text only from the html */ }
            <span className='subtitle'>{ moment.utc( n.publishedAt ).local().startOf( 'seconds' ).fromNow() }</span>
        </span>
    }, { key: `div-${ i }`, className: 'div-item', label: <div className='divider' /> } ] ) ), [ loading, notifications ] );

    return ( <>
            <Dropdown
                open={ open }
                menu={ { items } }
                placement="bottomRight"  
                overlayClassName={ classNames( 'scroll-fix dropdown header-dropdown noselect notification-dropdown', { mobile: isMobile, isOpen: open, isClosed: !open }, brand ) }
                >
                <a onClick={ toggleOpen } >
                    { open ? <BellFull/>  : <BellEmpty/> }
                    { unreadCount > 0 && <PaperIcon icon='Circle2' collection='ultimate' /> }
                </a>
            </Dropdown>
            <NotificationCarousel ref={ carouselRef } options={ { destroyOnClose: false } } onNotiRead={ localRead } loggingOut={ loggingOut } /> 
        </> );
} );

